import type { ExcludablePosition } from "./networth";

export type { ExcludablePosition };

export interface HistoryRow {
  stichtag: string;
  classes: {
    LIQUIDE: number;
    ANLAGEN: number;
    IMMOBILIE: number;
    SONSTIGE: number;
    VERBINDLICHKEITEN: number;
  };
  totalNetCents: number;
}

/**
 * Rechnet die ausgeschlossenen Positionen aus den History-Klassen heraus
 * und berechnet totalNetCents neu (Assets − Verbindlichkeiten).
 * Liefert eine neue Array — Input bleibt unveraendert.
 */
export function adjustClasses(
  history: HistoryRow[],
  excludablePositions: ExcludablePosition[],
  excludedIds: Set<string>,
): HistoryRow[] {
  if (excludedIds.size === 0) {
    return history.map((row) => ({
      ...row,
      totalNetCents: computeNetCents(row.classes),
    }));
  }
  const excluded = excludablePositions.filter((p) => excludedIds.has(p.id));
  return history.map((row) => {
    const newClasses = { ...row.classes };
    for (const pos of excluded) {
      const v = pos.perStichtag[row.stichtag] ?? 0;
      newClasses[pos.classKey] = (newClasses[pos.classKey] ?? 0) - v;
    }
    return {
      ...row,
      classes: newClasses,
      totalNetCents: computeNetCents(newClasses),
    };
  });
}

function computeNetCents(classes: HistoryRow["classes"]): number {
  return (
    classes.LIQUIDE +
    classes.ANLAGEN +
    classes.IMMOBILIE +
    classes.SONSTIGE -
    classes.VERBINDLICHKEITEN
  );
}

/**
 * Findet den naechstgelegenen Vorjahres-Snapshot fuer einen Ziel-Stichtag.
 * Suche um stichtag − 365d ± toleranceDays.
 */
export function findYoyMatch(
  history: HistoryRow[],
  targetStichtag: string,
  toleranceDays = 7,
): HistoryRow | null {
  const target = new Date(`${targetStichtag}T00:00:00.000Z`);
  const yoyTargetMs = target.getTime() - 365 * 24 * 60 * 60 * 1000;
  const toleranceMs = toleranceDays * 24 * 60 * 60 * 1000;

  let best: HistoryRow | null = null;
  let bestDiff = Number.POSITIVE_INFINITY;
  for (const row of history) {
    const ms = new Date(`${row.stichtag}T00:00:00.000Z`).getTime();
    const diff = Math.abs(ms - yoyTargetMs);
    if (diff <= toleranceMs && diff < bestDiff) {
      best = row;
      bestDiff = diff;
    }
  }
  return best;
}
